import { Global, css } from '@emotion/core';
import React from 'react';
import styled from '../../styled';

const StripSection = styled('section')<{ $variant?: 'default' | 'hero' }>`
  background-color: #fff;
  padding: ${(p) => (p.$variant === 'hero' ? '12px 0 32px' : '28px 0')};
  margin-top: ${(p) => (p.$variant === 'hero' ? '-28px' : '0')};
  position: relative;
  z-index: 1;
`;

const StripLabel = styled('p')`
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 0 16px;
`;

const LogosWrapper = styled('div')`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 56px;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, #fff 20%, rgba(255, 255, 255, 0));
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, #fff 20%, rgba(255, 255, 255, 0));
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      display: none;
    }
  }

  @media (max-width: 600px) {
    padding: 0 32px;

    &::before,
    &::after {
      width: 40px;
    }
  }
`;

const marqueeStyles = css`
  @keyframes publisherLogoMarquee {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const MarqueeTrack = styled('div')`
  display: flex;
  gap: 48px;
  width: fit-content;
  animation: publisherLogoMarquee 90s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    gap: 24px 40px;
  }

  @media (max-width: 600px) {
    gap: 32px;
  }
`;

const LOGO_HEIGHT = 22;

const Logo = styled('img')`
  height: ${LOGO_HEIGHT}px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
  opacity: 0.45;
  filter: grayscale(100%);
  flex-shrink: 0;
`;

const publisherLogos = [
  { src: '/images/logos/usa-today.svg', alt: 'USA Today' },
  { src: '/images/logos/business-insider.svg', alt: 'Business Insider' },
  { src: '/images/logos/theskimm.svg', alt: 'theSkimm' },
  { src: '/images/logos/people.svg', alt: 'People' },
  { src: '/images/logos/outside-inc.svg', alt: 'Outside Inc' },
  { src: '/images/logos/news-uk.svg', alt: 'News UK' },
  { src: '/images/logos/katie-couric-media.svg', alt: 'Katie Couric Media' },
  { src: '/images/logos/real-simple.svg', alt: 'Real Simple' },
  { src: '/images/logos/the-spruce.svg', alt: 'The Spruce' },
  { src: '/images/logos/mcclatchy-media.svg', alt: 'McClatchy Media' },
  { src: '/images/logos/ziff-davis.svg', alt: 'Ziff Davis' },
  { src: '/images/logos/everyday-health.svg', alt: 'Everyday Health' }
];

export const LogosSection = ({
  variant = 'default'
}: {
  variant?: 'default' | 'hero';
}) => {
  const duplicated = [...publisherLogos, ...publisherLogos];

  return (
    <StripSection $variant={variant} aria-label="Publishers using Affilimate">
      <Global styles={marqueeStyles} />
      <StripLabel>Live across 500+ premium properties</StripLabel>
      <LogosWrapper>
        <MarqueeTrack>
          {duplicated.map((logo, index) => (
            <Logo key={`${logo.src}-${index}`} src={logo.src} alt={logo.alt} />
          ))}
        </MarqueeTrack>
      </LogosWrapper>
    </StripSection>
  );
};
